import { monthKeyFromT } from '../engine/calendar';
import { driverInputUnit } from '../driverUtils';
import {
    formatCurrencyWhole,
    formatMonthLabel,
    formatPercent,
    formatSummaryMoney,
} from '../projectionsFormat';
import type {
    DriverType,
    Recipe,
    RecipeAnchor,
    RecipeGrowth,
    RecipeGrowthSteps,
    RecipeOverride,
    RecipePathSegment,
} from '../engine/types';
import type { FileIssue } from './types';
import { FILE_ISSUE_CODES } from './types';

const RECIPE_KEYS = new Set(['anchor', 'path', 'growth', 'seasonality', 'overrides', 'rationale']);
const ANCHOR_KEYS = new Set(['value', 'basis', 'months', 'runs', 'history_value']);
const PATH_KEYS = new Set(['to', 'by', 'shape']);
const GROWTH_KEYS = new Set(['per_month', 'per_year', 'steps']);
const GROWTH_STEPS_KEYS = new Set(['months', 'per_year']);
const SEASONALITY_KEYS = new Set(['factors', 'from_history']);
const FROM_HISTORY_KEYS = new Set(['years', 'amplitude', 'source_line']);
const OVERRIDE_KEYS = new Set(['value', 'reason', 'edited_by', 'edited_at']);

const NOT_ALLOWED_DRIVERS = new Set<DriverType>(['carry_forward', 'ignore', 's_curve']);
const PERCENT_DRIVERS = new Set<DriverType>([
    'mom_pct',
    'yoy_pct',
    'pct_gross_sales',
    'pct_gross_sales_growth',
    'pct_of_line',
]);

export interface MaterialiseContext {
    lastClosedMonth: string;
    driverType: DriverType;
    unit?: 'usd' | 'count' | 'pct';
    seasonalityFactors?: Record<string, number>;
    projectionMonths?: string[];
}

export interface ValidateRecipeContext {
    lastClosedMonth: string;
    driverType: DriverType;
    projectionMonths: string[];
    pathPrefix: string;
    rationaleMaxWords?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
}

function checkUnknownKeys(
    obj: Record<string, unknown>,
    allowed: Set<string>,
    path: string,
    issues: FileIssue[],
): void {
    for (const key of Object.keys(obj)) {
        if (key.startsWith('_')) {
            continue;
        }
        if (!allowed.has(key)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_unknown_key,
                severity: 'error',
                path: `${path}.${key}`,
                message: `Unknown field "${key}" at ${path}.`,
            });
        }
    }
}

function wordCount(text: string): number {
    const trimmed = text.trim();
    if (!trimmed) {
        return 0;
    }
    return trimmed.split(/\s+/).length;
}

function horizonYears(projectionMonths: string[]): Set<string> {
    const years = new Set<string>();
    for (const month of projectionMonths) {
        years.add(month.slice(0, 4));
    }
    return years;
}

function roundRecipeValue(value: number, driverType: DriverType, unit?: 'usd' | 'count' | 'pct'): number {
    const resolvedUnit = unit ?? driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]);
    const decimals =
        resolvedUnit === 'pct' ||
        PERCENT_DRIVERS.has(driverType) ||
        driverType === 'mom_pct' ||
        driverType === 'yoy_pct'
            ? 6
            : 2;
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

export function stepRateFromGrowth(steps: RecipeGrowthSteps): number {
    const n = steps.months.length;
    if (n === 0) {
        return 0;
    }
    return (1 + steps.per_year) ** (1 / n) - 1;
}

function isStepMonth(monthKey: string, steps: RecipeGrowthSteps): boolean {
    const month = Number(monthKey.slice(5, 7));
    return steps.months.includes(month);
}

function monthlyRateFromGrowth(
    growth: RecipeGrowth | undefined,
    monthKey: string,
): number {
    if (!growth) {
        return 0;
    }
    if (growth.steps) {
        return isStepMonth(monthKey, growth.steps) ? stepRateFromGrowth(growth.steps) : 0;
    }
    const year = monthKey.slice(0, 4);
    if (growth.per_month != null) {
        if (typeof growth.per_month === 'number') {
            return growth.per_month;
        }
        const rate = growth.per_month[year];
        return typeof rate === 'number' ? rate : 0;
    }
    if (growth.per_year != null) {
        let annual = 0;
        if (typeof growth.per_year === 'number') {
            annual = growth.per_year;
        } else {
            annual = growth.per_year[year] ?? 0;
        }
        return (1 + annual) ** (1 / 12) - 1;
    }
    return 0;
}

function baseAtPosition(
    anchorValue: number,
    path: RecipePathSegment[] | undefined,
    position: number,
    projectionMonths: string[],
): number {
    if (!path || path.length === 0) {
        return anchorValue;
    }

    const targets: Array<{ position: number; value: number }> = [{ position: 0, value: anchorValue }];
    for (const segment of path) {
        const t = projectionMonths.indexOf(segment.by);
        const pos = t >= 0 ? t + 1 : position;
        targets.push({ position: pos, value: segment.to });
    }

    if (position <= 0) {
        return anchorValue;
    }

    let segIdx = 0;
    for (let i = 0; i < targets.length - 1; i += 1) {
        if (targets[i + 1]!.position >= position) {
            segIdx = i;
            break;
        }
        segIdx = i;
    }

    const from = targets[segIdx]!;
    const to = targets[segIdx + 1] ?? targets[targets.length - 1]!;
    if (position >= to.position) {
        return to.value;
    }
    if (position <= from.position) {
        return from.value;
    }

    const segment = path[Math.min(segIdx, path.length - 1)]!;
    const shape = segment.shape ?? 'linear';
    if (shape === 'step') {
        return from.value;
    }
    const span = to.position - from.position;
    if (span === 0) {
        return to.value;
    }
    return from.value + (to.value - from.value) * ((position - from.position) / span);
}

function seasonFactor(
    monthKey: string,
    recipe: Recipe,
    seasonalityFactors?: Record<string, number>,
): number {
    const monthOfYear = String(Number(monthKey.slice(5, 7)));
    if (seasonalityFactors) {
        return seasonalityFactors[monthOfYear] ?? 1;
    }
    const factors = recipe.seasonality?.factors;
    if (!factors) {
        return 1;
    }
    return factors[monthOfYear] ?? 1;
}

function normaliseOverride(raw: number | RecipeOverride): RecipeOverride {
    if (typeof raw === 'number') {
        return { value: raw };
    }
    return raw;
}

export function materialise(recipe: Recipe, ctx: MaterialiseContext): number[] {
    const lastClosed =
        ctx.lastClosedMonth.length === 7
            ? `${ctx.lastClosedMonth}-01`
            : ctx.lastClosedMonth;
    const projectionMonths = ctx.projectionMonths ?? [];
    const months =
        projectionMonths.length === 36
            ? projectionMonths
            : Array.from({ length: 36 }, (_, i) => monthKeyFromT(lastClosed, i + 1));

    let level = 1;
    const out: number[] = [];
    for (let i = 0; i < 36; i += 1) {
        const position = i + 1;
        const monthKey = months[i]!;
        const base = baseAtPosition(recipe.anchor.value, recipe.path, position, months);
        const rate = monthlyRateFromGrowth(recipe.growth, monthKey);
        level *= 1 + rate;
        const season = seasonFactor(monthKey, recipe, ctx.seasonalityFactors);
        const overrideRaw = recipe.overrides?.[monthKey];
        let value = base * level * season;
        if (overrideRaw !== undefined) {
            value = normaliseOverride(overrideRaw).value;
        }
        out.push(roundRecipeValue(value, ctx.driverType, ctx.unit));
    }
    return out;
}

function validateGrowth(
    growth: RecipeGrowth,
    projectionMonths: string[],
    path: string,
    issues: FileIssue[],
): void {
    checkUnknownKeys(growth as Record<string, unknown>, GROWTH_KEYS, path, issues);
    const hasMonth = growth.per_month != null;
    const hasYear = growth.per_year != null;
    const hasSteps = growth.steps != null;
    const growthModes = [hasMonth, hasYear, hasSteps].filter(Boolean).length;
    if (growthModes > 1) {
        issues.push({
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path,
            message: 'growth cannot combine per_month, per_year, and steps.',
        });
        return;
    }
    if (hasSteps && growth.steps) {
        checkUnknownKeys(
            growth.steps as unknown as Record<string, unknown>,
            GROWTH_STEPS_KEYS,
            `${path}.steps`,
            issues,
        );
        const months = growth.steps.months;
        if (!Array.isArray(months) || months.length === 0) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${path}.steps.months`,
                message: 'growth.steps.months must be a non-empty array.',
            });
        } else {
            for (const month of months) {
                if (!Number.isInteger(month) || month < 1 || month > 12) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${path}.steps.months`,
                        message: 'growth.steps.months entries must be integers from 1 to 12.',
                    });
                    break;
                }
            }
        }
        if (coerceNumber(growth.steps.per_year) == null) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${path}.steps.per_year`,
                message: 'growth.steps.per_year must be numeric.',
            });
        }
    }
    if (hasSteps) {
        return;
    }
    const yearsNeeded = horizonYears(projectionMonths);
    const checkMap = (raw: unknown, field: string) => {
        if (typeof raw === 'number') {
            return;
        }
        if (!isObject(raw)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${path}.${field}`,
                message: `${field} must be a number or year map.`,
            });
            return;
        }
        for (const key of Object.keys(raw)) {
            if (!yearsNeeded.has(key)) {
                issues.push({
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${path}.${field}.${key}`,
                    message: `Growth year key "${key}" is outside the projection horizon.`,
                });
            }
        }
        for (const year of yearsNeeded) {
            if (!(year in raw)) {
                issues.push({
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${path}.${field}`,
                    message: `Growth ${field} map is missing year "${year}".`,
                });
            }
        }
    };
    if (hasMonth) {
        checkMap(growth.per_month, 'per_month');
    }
    if (hasYear) {
        checkMap(growth.per_year, 'per_year');
    }
}

export function validateRecipe(
    raw: unknown,
    ctx: ValidateRecipeContext,
): { recipe: Recipe | null; issues: FileIssue[] } {
    const issues: FileIssue[] = [];
    const { driverType, projectionMonths, pathPrefix } = ctx;
    const rationaleMax = ctx.rationaleMaxWords ?? 60;

    if (NOT_ALLOWED_DRIVERS.has(driverType)) {
        issues.push({
            code: FILE_ISSUE_CODES.file_recipe_not_allowed,
            severity: 'error',
            path: pathPrefix,
            message: `Driver ${driverType} does not accept recipes.`,
        });
        return { recipe: null, issues };
    }

    if (!isObject(raw)) {
        issues.push({
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path: pathPrefix,
            message: 'Recipe values must be an object with anchor.',
        });
        return { recipe: null, issues };
    }

    checkUnknownKeys(raw, RECIPE_KEYS, pathPrefix, issues);

    if (!isObject(raw.anchor)) {
        issues.push({
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path: `${pathPrefix}.anchor`,
            message: 'Recipe anchor is required.',
        });
        return { recipe: null, issues };
    }
    checkUnknownKeys(raw.anchor, ANCHOR_KEYS, `${pathPrefix}.anchor`, issues);

    const anchorValue = coerceNumber(raw.anchor.value);
    if (anchorValue === null) {
        issues.push({
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path: `${pathPrefix}.anchor.value`,
            message: 'anchor.value must be a finite number.',
        });
    }

    const basis = (raw.anchor.basis as RecipeAnchor['basis']) ?? 'manual';
    const validBasis = new Set([
        'manual',
        'last_actual',
        'trailing_avg',
        'last_actual_per_period',
        'trailing_avg_per_period',
    ]);
    if (!validBasis.has(basis)) {
        issues.push({
            code: FILE_ISSUE_CODES.file_values_shape,
            severity: 'error',
            path: `${pathPrefix}.anchor.basis`,
            message: `Invalid anchor basis "${String(raw.anchor.basis)}".`,
        });
    }
    if (
        (driverType === 'mom_pct' || driverType === 'yoy_pct') &&
        basis !== 'manual'
    ) {
        issues.push({
            code: FILE_ISSUE_CODES.file_recipe_basis_unsupported,
            severity: 'error',
            path: `${pathPrefix}.anchor.basis`,
            message: `${driverType} recipes require manual anchor basis.`,
        });
    }

    let months: 1 | 3 | 6 | 12 = 3;
    if (raw.anchor.months != null) {
        const m = Number(raw.anchor.months);
        if (![1, 3, 6, 12].includes(m)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.anchor.months`,
                message: 'anchor.months must be 1, 3, 6, or 12.',
            });
        } else {
            months = m as 1 | 3 | 6 | 12;
        }
    }

    let runs: 1 | 3 | 6 = 3;
    if (raw.anchor.runs != null) {
        const r = Number(raw.anchor.runs);
        if (![1, 3, 6].includes(r)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.anchor.runs`,
                message: 'anchor.runs must be 1, 3, or 6.',
            });
        } else {
            runs = r as 1 | 3 | 6;
        }
    }

    const pathSegments: RecipePathSegment[] = [];
    if (raw.path != null) {
        if (!Array.isArray(raw.path)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.path`,
                message: 'path must be an array.',
            });
        } else if (raw.path.length > 12) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.path`,
                message: 'path may have at most 12 segments.',
            });
        } else {
            let prevBy: string | null = null;
            for (let i = 0; i < raw.path.length; i += 1) {
                const segPath = `${pathPrefix}.path[${i}]`;
                const seg = raw.path[i];
                if (!isObject(seg)) {
                    continue;
                }
                checkUnknownKeys(seg, PATH_KEYS, segPath, issues);
                const to = coerceNumber(seg.to);
                if (to === null) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${segPath}.to`,
                        message: 'path segment to must be a finite number.',
                    });
                }
                const by = typeof seg.by === 'string' ? seg.by : '';
                if (!projectionMonths.includes(by)) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${segPath}.by`,
                        message: `path.by "${by}" is not a projection month.`,
                    });
                }
                if (prevBy !== null && by <= prevBy) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${segPath}.by`,
                        message: 'path.by months must be strictly increasing.',
                    });
                }
                prevBy = by;
                const shape = seg.shape;
                if (shape !== undefined && shape !== 'linear' && shape !== 'step') {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${segPath}.shape`,
                        message: 'path shape must be linear or step.',
                    });
                }
                if (to !== null && by) {
                    pathSegments.push({
                        to,
                        by,
                        shape: shape === 'step' ? 'step' : 'linear',
                    });
                }
            }
        }
    }

    if (raw.growth != null) {
        if (!isObject(raw.growth)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.growth`,
                message: 'growth must be an object.',
            });
        } else {
            validateGrowth(raw.growth as RecipeGrowth, projectionMonths, `${pathPrefix}.growth`, issues);
        }
    }

    let seasonalityFactors: Record<string, number> | undefined;
    if (raw.seasonality != null) {
        if (!isObject(raw.seasonality)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.seasonality`,
                message: 'seasonality must be an object.',
            });
        } else {
            checkUnknownKeys(raw.seasonality, SEASONALITY_KEYS, `${pathPrefix}.seasonality`, issues);
            const hasFactors = raw.seasonality.factors != null;
            const hasFromHistory = raw.seasonality.from_history != null;
            if (hasFactors && hasFromHistory) {
                issues.push({
                    code: FILE_ISSUE_CODES.file_values_shape,
                    severity: 'error',
                    path: `${pathPrefix}.seasonality`,
                    message: 'seasonality cannot include both factors and from_history.',
                });
            }
            if (hasFromHistory && isObject(raw.seasonality.from_history)) {
                checkUnknownKeys(
                    raw.seasonality.from_history,
                    FROM_HISTORY_KEYS,
                    `${pathPrefix}.seasonality.from_history`,
                    issues,
                );
                const years = Number(raw.seasonality.from_history.years);
                if (![1, 2, 3].includes(years)) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${pathPrefix}.seasonality.from_history.years`,
                        message: 'from_history.years must be 1, 2, or 3.',
                    });
                }
                const amp = raw.seasonality.from_history.amplitude;
                if (amp != null) {
                    const a = Number(amp);
                    if (!Number.isFinite(a) || a < 0 || a > 1) {
                        issues.push({
                            code: FILE_ISSUE_CODES.file_values_shape,
                            severity: 'error',
                            path: `${pathPrefix}.seasonality.from_history.amplitude`,
                            message: 'from_history.amplitude must be between 0 and 1.',
                        });
                    }
                }
            }
            if (hasFactors && isObject(raw.seasonality.factors)) {
                seasonalityFactors = {};
                for (const [key, val] of Object.entries(raw.seasonality.factors)) {
                    const n = coerceNumber(val);
                    if (n !== null) {
                        seasonalityFactors[key] = n;
                    }
                }
            }
        }
    }

    const overrides: Record<string, RecipeOverride> = {};
    if (raw.overrides != null) {
        if (!isObject(raw.overrides)) {
            issues.push({
                code: FILE_ISSUE_CODES.file_values_shape,
                severity: 'error',
                path: `${pathPrefix}.overrides`,
                message: 'overrides must be an object.',
            });
        } else {
            for (const [month, val] of Object.entries(raw.overrides)) {
                if (!projectionMonths.includes(month)) {
                    issues.push({
                        code: FILE_ISSUE_CODES.file_values_shape,
                        severity: 'error',
                        path: `${pathPrefix}.overrides["${month}"]`,
                        message: `Override month "${month}" is outside the projection horizon.`,
                    });
                    continue;
                }
                if (typeof val === 'number' || typeof val === 'string') {
                    const n = coerceNumber(val);
                    if (n === null) {
                        issues.push({
                            code: FILE_ISSUE_CODES.file_values_shape,
                            severity: 'error',
                            path: `${pathPrefix}.overrides["${month}"]`,
                            message: 'Override value must be numeric.',
                        });
                    } else {
                        overrides[month] = { value: n };
                    }
                } else if (isObject(val)) {
                    checkUnknownKeys(val, OVERRIDE_KEYS, `${pathPrefix}.overrides["${month}"]`, issues);
                    const n = coerceNumber(val.value);
                    if (n === null) {
                        issues.push({
                            code: FILE_ISSUE_CODES.file_values_shape,
                            severity: 'error',
                            path: `${pathPrefix}.overrides["${month}"].value`,
                            message: 'Override value must be numeric.',
                        });
                    } else {
                        overrides[month] = {
                            value: n,
                            reason: typeof val.reason === 'string' ? val.reason : undefined,
                            ...(typeof val.edited_by === 'string' ? { edited_by: val.edited_by } : {}),
                            ...(typeof val.edited_at === 'string' ? { edited_at: val.edited_at } : {}),
                        };
                    }
                }
            }
        }
    }

    if (typeof raw.rationale === 'string') {
        const words = wordCount(raw.rationale);
        if (words > 120) {
            issues.push({
                code: FILE_ISSUE_CODES.file_rationale_long,
                severity: 'error',
                path: `${pathPrefix}.rationale`,
                message: `rationale has ${words} words (max 120).`,
            });
        } else if (words > rationaleMax) {
            issues.push({
                code: FILE_ISSUE_CODES.file_rationale_long,
                severity: 'warning',
                path: `${pathPrefix}.rationale`,
                message: `rationale has ${words} words (recommended max ${rationaleMax}).`,
            });
        }
    }

    if (anchorValue !== null && PERCENT_DRIVERS.has(driverType)) {
        if (Math.abs(anchorValue) > 1) {
            issues.push({
                code: FILE_ISSUE_CODES.file_recipe_percent_scale,
                severity: 'warning',
                path: `${pathPrefix}.anchor.value`,
                message: 'anchor.value looks like whole percents; 5% is 0.05.',
            });
        }
        for (const seg of pathSegments) {
            if (Math.abs(seg.to) > 1) {
                issues.push({
                    code: FILE_ISSUE_CODES.file_recipe_percent_scale,
                    severity: 'warning',
                    path: `${pathPrefix}.path`,
                    message: 'path.to looks like whole percents; 5% is 0.05.',
                });
                break;
            }
        }
        for (const ov of Object.values(overrides)) {
            if (Math.abs(ov.value) > 1) {
                issues.push({
                    code: FILE_ISSUE_CODES.file_recipe_percent_scale,
                    severity: 'warning',
                    path: `${pathPrefix}.overrides`,
                    message: 'override value looks like whole percents; 5% is 0.05.',
                });
                break;
            }
        }
    }

    const growthRates: number[] = [];
    if (isObject(raw.growth)) {
        const g = raw.growth as RecipeGrowth;
        if (typeof g.per_month === 'number') {
            growthRates.push(g.per_month);
        } else if (isObject(g.per_month)) {
            growthRates.push(...Object.values(g.per_month).map((v) => Number(v)));
        }
        if (typeof g.per_year === 'number') {
            growthRates.push(g.per_year);
        } else if (isObject(g.per_year)) {
            growthRates.push(...Object.values(g.per_year).map((v) => Number(v)));
        }
    }
    if (growthRates.some((r) => Math.abs(r) > 0.5)) {
        issues.push({
            code: FILE_ISSUE_CODES.file_recipe_percent_scale,
            severity: 'warning',
            path: `${pathPrefix}.growth`,
            message: 'growth rate magnitude exceeds 0.5; confirm decimal fraction scale.',
        });
    }

    if (issues.some((i) => i.severity === 'error') || anchorValue === null) {
        return { recipe: null, issues };
    }

    const anchor: RecipeAnchor = {
        value: anchorValue,
        basis,
        ...(basis === 'trailing_avg' ? { months } : {}),
        ...(basis === 'trailing_avg_per_period' ? { runs } : {}),
        ...(raw.anchor.history_value != null
            ? { history_value: coerceNumber(raw.anchor.history_value) ?? undefined }
            : {}),
    };

    const recipe: Recipe = normaliseRecipe({
        anchor,
        ...(pathSegments.length > 0 ? { path: pathSegments } : {}),
        ...(raw.growth != null && isObject(raw.growth) ? { growth: raw.growth as RecipeGrowth } : {}),
        ...(raw.seasonality != null && isObject(raw.seasonality)
            ? {
                  seasonality: {
                      ...(seasonalityFactors ? { factors: seasonalityFactors } : {}),
                      ...(isObject(raw.seasonality.from_history)
                          ? {
                                from_history: {
                                    years: Number(raw.seasonality.from_history.years),
                                    amplitude:
                                        raw.seasonality.from_history.amplitude != null
                                            ? Number(raw.seasonality.from_history.amplitude)
                                            : undefined,
                                    source_line:
                                        typeof raw.seasonality.from_history.source_line === 'string'
                                            ? raw.seasonality.from_history.source_line
                                            : undefined,
                                },
                            }
                          : {}),
                  },
              }
            : {}),
        ...(Object.keys(overrides).length > 0 ? { overrides } : {}),
        ...(typeof raw.rationale === 'string' ? { rationale: raw.rationale } : {}),
    });

    return { recipe, issues };
}

function stripNullsDeep<T>(value: T): T {
    if (value === null || value === undefined) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map((item) => stripNullsDeep(item)) as T;
    }
    if (typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
            if (item != null) {
                out[key] = stripNullsDeep(item);
            }
        }
        return out as T;
    }
    return value;
}

/** Strip JSON `null` optional keys so server-shaped recipes match uploaded compact form. */
export function normaliseRecipe(recipe: Recipe): Recipe {
    return stripNullsDeep(recipe) as Recipe;
}

function projectionMonthsForCtx(ctx: MaterialiseContext): string[] {
    const lastClosed =
        ctx.lastClosedMonth.length === 7 ? `${ctx.lastClosedMonth}-01` : ctx.lastClosedMonth;
    const projectionMonths = ctx.projectionMonths ?? [];
    return projectionMonths.length === 36
        ? projectionMonths
        : Array.from({ length: 36 }, (_, i) => monthKeyFromT(lastClosed, i + 1));
}

function levelAt(recipe: Recipe, t: number, ctx: MaterialiseContext): number {
    const months = projectionMonthsForCtx(ctx);
    let level = 1;
    for (let i = 0; i < t; i += 1) {
        level *= 1 + monthlyRateFromGrowth(recipe.growth, months[i]!);
    }
    return level;
}

function anchorCoeffAtPosition(
    path: RecipePathSegment[] | undefined,
    position: number,
    projectionMonths: string[],
): { coeff: number; addend: number } {
    const baseAtOne = baseAtPosition(1, path, position, projectionMonths);
    const baseAtZero = baseAtPosition(0, path, position, projectionMonths);
    return { coeff: baseAtOne - baseAtZero, addend: baseAtZero };
}

export function solveAnchorValue(
    recipe: Recipe,
    t: number,
    target: number,
    ctx: MaterialiseContext,
): number {
    const months = projectionMonthsForCtx(ctx);
    const level = levelAt(recipe, t, ctx);
    const monthKey = months[t - 1]!;
    const season = seasonFactor(monthKey, recipe, ctx.seasonalityFactors);
    const desiredBase = target / (level * season);
    const { coeff, addend } = anchorCoeffAtPosition(recipe.path, t, months);
    if (Math.abs(coeff) < 1e-12) {
        return roundRecipeValue(desiredBase, ctx.driverType, ctx.unit);
    }
    return roundRecipeValue((desiredBase - addend) / coeff, ctx.driverType, ctx.unit);
}

export function recipeValueBeforeOverride(
    recipe: Recipe,
    t: number,
    ctx: MaterialiseContext,
): number {
    const monthKey = projectionMonthsForCtx(ctx)[t - 1]!;
    const without: Recipe = {
        ...recipe,
        overrides: recipe.overrides
            ? Object.fromEntries(
                  Object.entries(recipe.overrides).filter(([key]) => key !== monthKey),
              )
            : undefined,
    };
    return materialise(without, ctx)[t - 1]!;
}

/** Growth rates are decimal fractions per month (0.0035 = 0.35%/mo); not the 1-dp sales % formatter. */
function formatGrowthRate(rate: number): string {
    const pct = rate * 100;
    const abs = Math.abs(pct).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${pct < 0 ? '-' : '+'}${abs}%`;
}

function compactRecipeValue(
    value: number,
    driverType: DriverType,
    unit?: 'usd' | 'count' | 'pct',
): string {
    const resolvedUnit = unit ?? driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]);
    if (
        resolvedUnit === 'pct' ||
        PERCENT_DRIVERS.has(driverType) ||
        driverType === 'mom_pct' ||
        driverType === 'yoy_pct'
    ) {
        return formatPercent(value);
    }
    if (driverType === 'dio' || driverType === 'dso' || driverType === 'dpo') {
        return `${value} days`;
    }
    if (resolvedUnit === 'count') {
        return String(value);
    }
    return formatSummaryMoney(value);
}

export function recipeSubline(recipe: Recipe, ctx: MaterialiseContext): string | null {
    const { driverType } = ctx;
    const anchorCompact = compactRecipeValue(recipe.anchor.value, driverType, ctx.unit);

    if (recipe.path && recipe.path.length > 0) {
        const last = recipe.path[recipe.path.length - 1]!;
        const toCompact = compactRecipeValue(last.to, driverType, ctx.unit);
        const byLabel = formatMonthLabel(last.by);
        return `${anchorCompact} → ${toCompact} by ${byLabel}`;
    }

    if (recipe.growth) {
        const months = projectionMonthsForCtx(ctx);
        if (recipe.growth.steps) {
            const rate = stepRateFromGrowth(recipe.growth.steps);
            const monthLabels = recipe.growth.steps.months
                .map((month) => formatMonthLabel(`2026-${String(month).padStart(2, '0')}`).slice(0, 3))
                .join(' & ');
            const periodSuffix = driverType === 'dollar_per_pay_period' ? ' /period' : '';
            return `${anchorCompact}${periodSuffix}, raise ${formatGrowthRate(rate)} (${monthLabels})`;
        }
        const rate = monthlyRateFromGrowth(recipe.growth, months[0]!);
        const periodSuffix = driverType === 'dollar_per_pay_period' ? ' /period' : '';
        return `${anchorCompact}${periodSuffix}, ${formatGrowthRate(rate)}/mo`;
    }

    if (recipe.seasonality) {
        const perDay =
            driverType === 'count_per_open_day' || driverType === 'dollar_per_open_day';
        const suffix = perDay ? '/day × seasonal' : ' × seasonal';
        return `${anchorCompact}${suffix}`;
    }

    if (driverType === 'dio' || driverType === 'dso' || driverType === 'dpo') {
        return `${recipe.anchor.value} days`;
    }

    return null;
}

export interface ExplainRecipeBlock {
    anchor: string;
    step: string | null;
    growth: string | null;
    season: string | null;
    override: null | {
        summary: string;
        reason: string | null;
        monthKey: string;
        edited_by?: string | null;
        edited_at?: string | null;
    };
}

function anchorBasisLabel(anchor: RecipeAnchor, lastClosedMonth: string): string {
    const closed =
        lastClosedMonth.length >= 7 ? lastClosedMonth.slice(0, 7) : lastClosedMonth;
    switch (anchor.basis) {
        case 'last_actual':
            return `${formatMonthLabel(closed)} actual`;
        case 'trailing_avg': {
            const months = anchor.months ?? 3;
            const end = closed;
            const start = monthKeyFromT(closed.length === 7 ? `${closed}-01` : closed, -(months - 1));
            const yy = String(Number(end.slice(0, 4)) % 100).padStart(2, '0');
            const startAbbrev = formatMonthLabel(start).split('-')[0] ?? start;
            const endAbbrev = formatMonthLabel(end).split('-')[0] ?? end;
            return `${startAbbrev}–${endAbbrev} ${yy} avg`;
        }
        case 'last_actual_per_period':
            return `${formatMonthLabel(closed)} per period`;
        case 'trailing_avg_per_period':
            return `${anchor.runs ?? 3}-run avg`;
        case 'manual':
        default:
            return 'manual';
    }
}

function formatAnchorExplain(
    recipe: Recipe,
    driverType: DriverType,
    lastClosedMonth: string,
    unit?: 'usd' | 'count' | 'pct',
): string {
    const valueStr = compactRecipeValue(recipe.anchor.value, driverType, unit);
    const basisLabel = anchorBasisLabel(recipe.anchor, lastClosedMonth);
    const history =
        recipe.anchor.history_value != null
            ? compactRecipeValue(recipe.anchor.history_value, driverType, unit)
            : null;
    if (recipe.anchor.basis === 'manual' || history == null) {
        return `${valueStr} (${basisLabel})`;
    }
    return `${valueStr} (${basisLabel}: ${history})`;
}

function pathStepExplain(
    recipe: Recipe,
    t: number,
    ctx: MaterialiseContext,
): string | null {
    const path = recipe.path;
    if (!path || path.length === 0) {
        return null;
    }
    const months = projectionMonthsForCtx(ctx);
    const position = t;
    const targets: Array<{ position: number; value: number }> = [
        { position: 0, value: recipe.anchor.value },
    ];
    for (const segment of path) {
        const idx = months.indexOf(segment.by);
        targets.push({ position: idx >= 0 ? idx + 1 : position, value: segment.to });
    }

    let segIdx = 0;
    for (let i = 0; i < targets.length - 1; i += 1) {
        if (targets[i + 1]!.position >= position) {
            segIdx = i;
            break;
        }
        segIdx = i;
    }

    const from = targets[segIdx]!;
    const to = targets[segIdx + 1];
    if (!to) {
        return null;
    }

    if (position < from.position) {
        const seg = path[Math.min(segIdx, path.length - 1)]!;
        const atLabel = formatMonthLabel(seg.by);
        const toStr = compactRecipeValue(seg.to, ctx.driverType, ctx.unit);
        return `step to ${toStr} at ${atLabel}, not yet reached`;
    }

    const segment = path[Math.min(segIdx, path.length - 1)]!;
    const shape = segment.shape ?? 'linear';
    const byLabel = formatMonthLabel(segment.by);

    if (shape === 'step' && position < to.position) {
        return `step to ${compactRecipeValue(segment.to, ctx.driverType, ctx.unit)} at ${byLabel}, not yet reached`;
    }

    if (shape === 'linear' && position < to.position) {
        const span = to.position - from.position;
        const k = position - from.position;
        const isPct =
            PERCENT_DRIVERS.has(ctx.driverType) ||
            ctx.unit === 'pct' ||
            ctx.driverType === 'mom_pct' ||
            ctx.driverType === 'yoy_pct';
        const delta = to.value - from.value;
        const deltaStr = isPct
            ? `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(3)} pts`
            : `${delta >= 0 ? '+' : ''}${formatSummaryMoney(delta)}`;
        const increment = span > 0 ? delta / span : 0;
        const incStr = isPct
            ? `${increment >= 0 ? '+' : ''}${(increment * 100).toFixed(3)} pts`
            : `${increment >= 0 ? '+' : ''}${formatSummaryMoney(increment)}`;
        return `glide ${deltaStr} by ${byLabel} · month ${k} of ${span} → ${incStr}`;
    }

    return null;
}

function growthExplain(recipe: Recipe, t: number, ctx: MaterialiseContext): string | null {
    if (!recipe.growth) {
        return null;
    }
    const months = projectionMonthsForCtx(ctx);
    const lastClosed =
        ctx.lastClosedMonth.length === 7 ? ctx.lastClosedMonth.slice(0, 7) : ctx.lastClosedMonth.slice(0, 7);
    const sinceLabel = formatMonthLabel(lastClosed);
    const monthKey = months[t - 1]!;
    if (recipe.growth.steps) {
        const rate = stepRateFromGrowth(recipe.growth.steps);
        const level = levelAt(recipe, t, ctx);
        if (isStepMonth(monthKey, recipe.growth.steps)) {
            return `raise ${formatGrowthRate(rate)} since ${sinceLabel} → ×${level.toFixed(4)}`;
        }
        return `raise ${formatGrowthRate(rate)} on step months → ×${level.toFixed(4)}`;
    }
    const rate = monthlyRateFromGrowth(recipe.growth, monthKey);
    const level = levelAt(recipe, t, ctx);
    return `${formatGrowthRate(rate)}/mo since ${sinceLabel} → ×${level.toFixed(4)}`;
}

function seasonExplain(recipe: Recipe, t: number, ctx: MaterialiseContext): string | null {
    const months = projectionMonthsForCtx(ctx);
    const monthKey = months[t - 1]!;
    const factor = seasonFactor(monthKey, recipe, ctx.seasonalityFactors);
    if (Math.abs(factor - 1) < 1e-9) {
        return null;
    }
    const mon = formatMonthLabel(monthKey).slice(0, 3);
    return `${mon} factor ${factor}`;
}

export function explainRecipe(
    recipe: Recipe,
    t: number,
    ctx: MaterialiseContext,
): ExplainRecipeBlock {
    const months = projectionMonthsForCtx(ctx);
    const monthKey = months[t - 1]!;
    const anchor = formatAnchorExplain(recipe, ctx.driverType, ctx.lastClosedMonth, ctx.unit);
    const step = pathStepExplain(recipe, t, ctx);
    const growth = growthExplain(recipe, t, ctx);
    const season = seasonExplain(recipe, t, ctx);

    const overrideRaw = recipe.overrides?.[monthKey];
    let override: ExplainRecipeBlock['override'] = null;
    if (overrideRaw !== undefined) {
        const ov = normaliseOverride(overrideRaw);
        const recipeVal = recipeValueBeforeOverride(recipe, t, ctx);
        const editedBy = ov.edited_by ?? 'unknown';
        const editedDate = ov.edited_at ? ov.edited_at.slice(0, 10) : '—';
        const reason = ov.reason ?? null;
        const summary =
            `Override ${formatCurrencyWhole(ov.value)} (recipe ${formatCurrencyWhole(recipeVal)})` +
            ` — ${editedBy}, ${editedDate}` +
            (reason ? `: ${reason}` : '');
        override = {
            summary,
            reason,
            monthKey,
            edited_by: ov.edited_by,
            edited_at: ov.edited_at,
        };
    }

    return { anchor, step, growth, season, override };
}
